/** 
 * @TercioSantos-1 |
 * api/get/todas as configurações de 1 empresa |
 * api/get/1 configuração específica |
 * api/put/atualização de 1 configuração |
 *
 * Cache em módulo com TTL e deduplicação de chamadas em voo.
 *
 * Motivo: `get({ column })` fazia um GET completo a cada configuração lida, sem
 * memória. Abrir um ticket disparava uma leitura por componente (Ticket,
 * MessagesList, MessageInput, TicketActionButtons, ContactDrawer,
 * TicketCommercialItems) — cada uma um request inteiro para buscar UMA coluna.
 *
 * Mesmo padrão já validado em hooks/useCompanyModules. O `update` invalida o
 * cache na hora, então salvar uma configuração continua refletindo na tela.
 */
import { useCallback } from "react";
import api from "../../services/api";

const TTL_MS = 30000;

// chave -> { at, value } | chave -> Promise (em voo)
const cache = new Map();
const inflight = new Map();

const readCache = (key) => {
    const hit = cache.get(key);
    if (!hit) return undefined;
    if (Date.now() - hit.at > TTL_MS) {
        cache.delete(key);
        return undefined;
    }
    return hit.value;
};

const writeCache = (key, value) => {
    cache.set(key, { at: Date.now(), value });
};

// Uma configuração mudou: o cache inteiro cai. É barato (próxima leitura
// refaz 1 request) e evita ter que mapear quais chaves dependem de qual coluna.
export const clearCompanySettingsCache = () => {
    cache.clear();
    inflight.clear();
};

const dedupe = (key, loader) => {
    const pending = inflight.get(key);
    if (pending) return pending;

    const promise = loader()
        .then((value) => {
            writeCache(key, value);
            return value;
        })
        .finally(() => {
            inflight.delete(key);
        });

    inflight.set(key, promise);
    return promise;
};

const useCompanySettings = () => {
    const hasAuthToken = useCallback(() => {
        const token = localStorage.getItem("token");
        return !!(token && token !== "undefined" && token !== "null");
    }, []);

    const getAll = useCallback(async (company_id) => {
        if (!company_id || !hasAuthToken()) {
            return {};
        }

        const key = `all:${company_id}`;
        const cached = readCache(key);
        if (cached !== undefined) return cached;

        return dedupe(key, async () => {
            const { data } = await api.request({
                url: `/companySettings/${company_id}`,
                method: 'GET'
            });
            return data;
        });
    }, [hasAuthToken]);

    const get = useCallback(async (params) => {
        if (!hasAuthToken()) {
            return {};
        }

        const key = `one:${JSON.stringify(params || {})}`;
        const cached = readCache(key);
        if (cached !== undefined) return cached;

        return dedupe(key, async () => {
            const { data } = await api.request({
                url: '/companySettingOne',
                method: 'GET',
                params
            });
            return data;
        });
    }, [hasAuthToken]); 

    const update = useCallback(async (data) => {
        if (!hasAuthToken()) {
            return null;
        }

        const { data: responseData } = await api.request({
            url: '/companySettings',
            method: 'PUT',
            data
        });

        clearCompanySettingsCache();

        return responseData;
    }, [hasAuthToken]);

    return {
        getAll,
        get,
        update
    }
}

export default useCompanySettings;
